import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Globe,
  FileText,
  Settings,
  ChevronRight,
  Clock,
  Sparkles,
  Flower2,
  Camera,
  Mail,
  Music,
  ShieldCheck,
  Copy,
  ExternalLink,
  AlertCircle,
  Calendar,
  Phone,
  User,
  Check,
  CheckCircle2,
  QrCode,
  Share2,
  X,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  PlusCircle,
  Lock,
  ArrowLeft,
  Heart,
  Send,
  Eye,
  AlertTriangle,
  History,
  MessageSquare,
  Search,
  ArrowRight,
  HelpCircle,
  CheckCircle,
} from 'lucide-react';
import {
  CoupleSettings,
  MyWebRequest,
  WebRequestStatus,
  VisitorInfo,
  getWebRequestPhaseInfo,
} from '../types';
import { romanticAudio } from '../utils/audio';
import {
  searchWebRequestsByCodeOrPhone,
  subscribeToSingleWebRequest,
  updateUserWebRequestInDb,
  submitUserChangesForWebRequest,
} from '../services/firebaseDb';

export interface CountryCodeOption {
  code: string;
  name: string;
  flag: string;
}

export const COUNTRY_CODES: CountryCodeOption[] = [
  { code: '+57', name: 'Colombia', flag: '🇨🇴' },
  { code: '+52', name: 'México', flag: '🇲🇽' },
  { code: '+34', name: 'España', flag: '🇪🇸' },
  { code: '+54', name: 'Argentina', flag: '🇦🇷' },
  { code: '+56', name: 'Chile', flag: '🇨🇱' },
  { code: '+51', name: 'Perú', flag: '🇵🇪' },
  { code: '+593', name: 'Ecuador', flag: '🇪🇨' },
  { code: '+58', name: 'Venezuela', flag: '🇻🇪' },
  { code: '+1', name: 'EE. UU. / Canadá', flag: '🇺🇸' },
  { code: '+502', name: 'Guatemala', flag: '🇬🇹' },
  { code: '+503', name: 'El Salvador', flag: '🇸🇻' },
  { code: '+504', name: 'Honduras', flag: '🇭🇳' },
  { code: '+505', name: 'Nicaragua', flag: '🇳🇮' },
  { code: '+506', name: 'Costa Rica', flag: '🇨🇷' },
  { code: '+507', name: 'Panamá', flag: '🇵🇦' },
  { code: '+591', name: 'Bolivia', flag: '🇧🇴' },
  { code: '+595', name: 'Paraguay', flag: '🇵🇾' },
  { code: '+598', name: 'Uruguay', flag: '🇺🇾' },
  { code: '+1809', name: 'Rep. Dominicana', flag: '🇩🇴' },
  { code: '+55', name: 'Brasil', flag: '🇧🇷' },
  { code: '+', name: 'Otro país (+)', flag: '🌐' },
];

export interface RealFeatureDefinition {
  id: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accentColor: string;
}

export const REAL_AVAILABLE_FEATURES: RealFeatureDefinition[] = [
  {
    id: 'garden',
    label: 'Jardín Interactivo',
    description: 'Flores virtuales, rosas, tulipanes, pétalos animados y dedicatorias.',
    icon: Flower2,
    accentColor: 'text-[#E83E78] bg-[#FFF4F7] border-[#F0DDE4]',
  },
  {
    id: 'gallery',
    label: 'Álbum & Recuerdos',
    description: 'Galería fotográfica y momentos románticos de la pareja.',
    icon: Camera,
    accentColor: 'text-[#E85D8E] bg-[#FFF4F7] border-[#F0DDE4]',
  },
  {
    id: 'letters',
    label: 'Cartas con Cera',
    description: 'Cartas de amor íntimas protegidas con animación de sello de cera.',
    icon: Mail,
    accentColor: 'text-[#E83E78] bg-[#FFF4F7] border-[#F0DDE4]',
  },
  {
    id: 'music',
    label: 'Música Romántica',
    description: 'Reproductor con su canción o melodía especial de fondo.',
    icon: Music,
    accentColor: 'text-purple-700 bg-[#EDE7FF] border-[#F0DDE4]',
  },
  {
    id: 'quotes',
    label: 'Frases & Dedicatoria',
    description: 'Frases de amor dinámicas y dedicatoria personalizada en la portada.',
    icon: Sparkles,
    accentColor: 'text-[#E85D8E] bg-[#FFF4F7] border-[#F0DDE4]',
  },
  {
    id: 'counter',
    label: 'Contador Juntos',
    description: 'Contador en tiempo real exacto de días, horas y minutos juntos.',
    icon: Clock,
    accentColor: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  },
  {
    id: 'qr',
    label: 'Código QR & Enlace',
    description: 'Enlace privado y código QR listo para compartir en WhatsApp.',
    icon: QrCode,
    accentColor: 'text-sky-700 bg-sky-50 border-sky-200',
  },
];

const LOCAL_STORAGE_LAST_CODE = 'nuestros_recuerdos_last_request_code';
const LOCAL_STORAGE_LAST_ID = 'nuestros_recuerdos_last_request_id';

type ActiveViewMode = 'home' | 'create' | 'search' | 'status' | 'created_success';

interface MyWebViewProps {
  settings: CoupleSettings;
  currentVisitor?: VisitorInfo | null;
  requests: MyWebRequest[];
  currentUser?: any;
  isAdminAuthenticated?: boolean;
  onSubmitRequest: (request: Omit<MyWebRequest, 'id' | 'createdAt'>) => Promise<MyWebRequest>;
  onUpdateRequest?: (id: string, data: Partial<MyWebRequest>) => Promise<void>;
  onGoToGarden: () => void;
  onOpenPersonalizedWeb?: (request: MyWebRequest) => void;
}

export const MyWebView: React.FC<MyWebViewProps> = ({
  settings,
  currentVisitor,
  requests,
  currentUser,
  isAdminAuthenticated,
  onSubmitRequest,
  onUpdateRequest,
  onGoToGarden,
  onOpenPersonalizedWeb,
}) => {
  // Navigation & View state
  const [viewMode, setViewMode] = useState<ActiveViewMode>('home');
  const [activeRequest, setActiveRequest] = useState<MyWebRequest | null>(null);
  const [lastSavedCode, setLastSavedCode] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<MyWebRequest[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Form creation state
  const [applicantName, setApplicantName] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [countryCode, setCountryCode] = useState('+57');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(() => currentUser?.email || '');
  const [anniversaryDate, setAnniversaryDate] = useState('');
  const [dedication, setDedication] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([
    'garden',
    'gallery',
    'letters',
    'music',
    'quotes',
    'counter',
    'qr',
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [createdRequestInfo, setCreatedRequestInfo] = useState<MyWebRequest | null>(null);

  // Corrections / Changes requested state
  const [isEditingChanges, setIsEditingChanges] = useState(false);
  const [changesApplicantName, setChangesApplicantName] = useState('');
  const [changesPartnerName, setChangesPartnerName] = useState('');
  const [changesEmail, setChangesEmail] = useState('');
  const [changesAnniversaryDate, setChangesAnniversaryDate] = useState('');
  const [changesDedication, setChangesDedication] = useState('');
  const [changesUserNotes, setChangesUserNotes] = useState('');
  const [isSubmittingChanges, setIsSubmittingChanges] = useState(false);
  const [changesSuccessMessage, setChangesSuccessMessage] = useState<string | null>(null);

  // UI Modals & Actions
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [isSyncingLive, setIsSyncingLive] = useState(false);

  // Ref to hold unsubscribe function for active request live sync
  const unsubRequestRef = useRef<(() => void) | null>(null);

  // Initialize last saved code from localStorage
  useEffect(() => {
    try {
      const storedCode = localStorage.getItem(LOCAL_STORAGE_LAST_CODE);
      if (storedCode) {
        setLastSavedCode(storedCode.toUpperCase());
      }
    } catch {}
  }, []);

  // Sync active request from props if available and none selected yet
  useEffect(() => {
    if (!activeRequest && requests && requests.length > 0) {
      // 1. By authenticated user UID
      if (currentUser?.uid) {
        const byUid = requests.find((r) => r.ownerUid === currentUser.uid);
        if (byUid) {
          setActiveRequest(byUid);
          return;
        }
      }
      // 2. By last saved order code
      const storedCode = localStorage.getItem(LOCAL_STORAGE_LAST_CODE);
      if (storedCode) {
        const byCode = requests.find((r) => r.orderCode?.toUpperCase() === storedCode.toUpperCase());
        if (byCode) {
          setActiveRequest(byCode);
          return;
        }
      }
      // 3. By visitor ID
      const vid = currentVisitor?.visitorId;
      if (vid) {
        const byVid = requests.find((r) => r.visitorId === vid);
        if (byVid) {
          setActiveRequest(byVid);
          return;
        }
      }
    }
  }, [requests, currentUser, currentVisitor, activeRequest]);

  // Subscribe to real-time changes on activeRequest from Firestore
  useEffect(() => {
    if (unsubRequestRef.current) {
      unsubRequestRef.current();
      unsubRequestRef.current = null;
    }

    if (!activeRequest?.id && !activeRequest?.orderCode) return;

    const targetKey = activeRequest.id || activeRequest.orderCode;
    setIsSyncingLive(true);

    const unsub = subscribeToSingleWebRequest(
      targetKey,
      (updatedReq) => {
        setIsSyncingLive(false);
        if (updatedReq) {
          setActiveRequest(updatedReq);
        }
      },
      (err) => {
        setIsSyncingLive(false);
        console.warn('Real-time subscription notice for web request:', err);
      }
    );

    unsubRequestRef.current = unsub;

    return () => {
      if (unsubRequestRef.current) {
        unsubRequestRef.current();
        unsubRequestRef.current = null;
      }
    };
  }, [activeRequest?.id, activeRequest?.orderCode]);

  // Sync edit changes inputs when active request changes
  useEffect(() => {
    if (activeRequest) {
      setChangesApplicantName(activeRequest.applicantName || '');
      setChangesPartnerName(activeRequest.partnerName || '');
      setChangesEmail(activeRequest.email || activeRequest.partner1Email || '');
      setChangesAnniversaryDate(activeRequest.anniversaryDate || '');
      setChangesDedication(activeRequest.dedication || '');
      setChangesUserNotes(activeRequest.userNotes || '');
    }
  }, [activeRequest]);

  // Phase Info for active request
  const phaseInfo = useMemo(() => {
    if (!activeRequest) {
      return {
        phase: 0,
        key: 'sin_solicitud',
        title: 'Sin solicitud',
        subtitle: 'Aún no has registrado una solicitud para tu sitio web.',
        isApproved: false,
        isPublished: false,
      };
    }
    return getWebRequestPhaseInfo(activeRequest.status);
  }, [activeRequest]);

  // Status visual badge styling
  const statusBadgeStyle = useMemo(() => {
    if (!activeRequest) {
      return {
        bg: 'bg-stone-800/90',
        text: 'text-stone-300',
        border: 'border-stone-700/60',
        dot: 'bg-stone-500',
        label: 'Sin solicitud',
      };
    }

    const st = (activeRequest.status || 'enviado').toLowerCase();

    if (st === 'publicado') {
      return {
        bg: 'bg-emerald-50',
        text: 'text-emerald-800',
        border: 'border-emerald-200',
        dot: 'bg-emerald-500 animate-pulse',
        label: 'Publicado y En Línea',
      };
    }
    if (st === 'aprobado' || st === 'approved') {
      return {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        dot: 'bg-emerald-500 animate-pulse',
        label: 'Aprobado',
      };
    }
    if (st === 'cambios_solicitados') {
      return {
        bg: 'bg-amber-50',
        text: 'text-amber-800',
        border: 'border-amber-200',
        dot: 'bg-amber-500 animate-ping',
        label: 'Cambios solicitados',
      };
    }
    if (st === 'en_revision') {
      return {
        bg: 'bg-[#EDE7FF]',
        text: 'text-purple-800',
        border: 'border-[#F0DDE4]',
        dot: 'bg-purple-500 animate-pulse',
        label: 'En revisión',
      };
    }
    if (st === 'recibido') {
      return {
        bg: 'bg-[#FFF4F7]',
        text: 'text-[#E83E78]',
        border: 'border-[#F0DDE4]',
        dot: 'bg-[#E83E78] animate-pulse',
        label: 'Recibido',
      };
    }
    if (st === 'rejected' || st === 'rechazado') {
      return {
        bg: 'bg-rose-50',
        text: 'text-rose-800',
        border: 'border-rose-200',
        dot: 'bg-rose-500',
        label: 'Rechazado',
      };
    }
    return {
      bg: 'bg-[#FFF4F7]',
      text: 'text-[#E83E78]',
      border: 'border-[#F0DDE4]',
      dot: 'bg-[#E83E78] animate-pulse',
      label: 'Solicitud enviada',
    };
  }, [activeRequest]);

  // URL of active personalized website
  const personalWebUrl = useMemo(() => {
    if (!activeRequest?.orderCode) return '';
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/?web=${encodeURIComponent(activeRequest.orderCode)}`;
    }
    return `/?web=${encodeURIComponent(activeRequest.orderCode)}`;
  }, [activeRequest?.orderCode]);

  // QR code image URL
  const qrCodeImageUrl = useMemo(() => {
    if (!personalWebUrl) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(
      personalWebUrl
    )}&margin=12`;
  }, [personalWebUrl]);

  // Date formatting helper
  const formatTimestamp = (isoString?: string) => {
    if (!isoString) return 'Pendiente';
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Recientemente';
    }
  };

  // Copy share URL
  const handleCopyLink = () => {
    if (!personalWebUrl) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(personalWebUrl);
      setCopiedLink(true);
      romanticAudio.playBloomSuccess();
      setTimeout(() => setCopiedLink(false), 2500);
    }
  };

  // Copy Order Code
  const handleCopyCode = (code: string) => {
    if (!code) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(code);
      setCopiedCode(true);
      romanticAudio.playBloomSuccess();
      setTimeout(() => setCopiedCode(false), 2500);
    }
  };

  // Share via WhatsApp
  const handleShareWhatsApp = () => {
    if (!activeRequest || !personalWebUrl) return;
    const partner = activeRequest.partnerName || 'mi amor';
    const text = encodeURIComponent(
      `Hola ${partner} ❤️✨, creé un sitio web interactivo especial para nosotros dos:\n\n${personalWebUrl}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  // Toggle feature selection
  const handleToggleFeature = (featureId: string) => {
    romanticAudio.playNote(440, 0.08, 'sine', 0.03);
    setSelectedFeatures((prev) =>
      prev.includes(featureId) ? prev.filter((id) => id !== featureId) : [...prev, featureId]
    );
  };

  // Submit new request to Firestore
  const handleCreateRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const cleanApplicant = applicantName.trim();
    const cleanPartner = partnerName.trim();
    if (!cleanApplicant) {
      setFormError('Por favor ingresa tu nombre (o el del solicitante).');
      return;
    }
    if (!cleanPartner) {
      setFormError('Por favor ingresa el nombre de tu pareja.');
      return;
    }

    const cleanDigits = phone.replace(/[^0-9]/g, '');
    if (cleanDigits.length < 7) {
      setFormError('Por favor ingresa un número de teléfono / WhatsApp válido (mínimo 7 dígitos).');
      return;
    }

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@') || !cleanEmail.includes('.')) {
      setFormError('Por favor ingresa un correo electrónico válido para tu cuenta de acceso.');
      return;
    }

    const fullPhone = phone.trim().startsWith('+')
      ? phone.trim()
      : `${countryCode} ${phone.trim()}`;

    setIsSubmitting(true);
    try {
      const randomSuffix = Math.floor(1000 + Math.random() * 9000);
      const generatedCode = `WEB-${randomSuffix}`;
      const autoApprove = Boolean(settings.myWebConfig?.autoApproval);

      const created = await onSubmitRequest({
        orderCode: generatedCode,
        applicantName: cleanApplicant,
        partnerName: cleanPartner,
        whatsappPhone: fullPhone,
        email: cleanEmail,
        partner1Email: cleanEmail,
        anniversaryDate: anniversaryDate || undefined,
        dedication: dedication.trim() || undefined,
        desiredFeatures: selectedFeatures,
        notes: notes.trim() || undefined,
        status: 'recibido',
        visitorId: currentVisitor?.visitorId || undefined,
        ip: currentVisitor?.ip && currentVisitor.ip !== 'IP no detectada' ? currentVisitor.ip : undefined,
        deviceModel: currentVisitor?.model || currentVisitor?.os || undefined,
      });

      // Save locally for quick retrieval
      try {
        localStorage.setItem(LOCAL_STORAGE_LAST_CODE, generatedCode);
        if (created?.id) {
          localStorage.setItem(LOCAL_STORAGE_LAST_ID, created.id);
        }
        setLastSavedCode(generatedCode);
      } catch {}

      setCreatedRequestInfo(created);
      setActiveRequest(created);
      setViewMode('created_success');
      romanticAudio.playBloomSuccess();
    } catch (err: any) {
      console.error('Error submitting request to Firestore:', err);
      setFormError(err?.message || 'Error al guardar la solicitud en Firestore. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Search requests in Firestore by Code or Phone
  const handlePerformSearch = async (e?: React.FormEvent, directTerm?: string) => {
    if (e) e.preventDefault();
    const term = (directTerm !== undefined ? directTerm : searchQuery).trim();
    if (!term) {
      setSearchError('Por favor ingresa tu número de orden o número de teléfono.');
      return;
    }

    setSearchError(null);
    setIsSearching(true);
    setSearchResults(null);
    romanticAudio.playNote(400, 0.08, 'sine', 0.03);

    try {
      const results = await searchWebRequestsByCodeOrPhone(term);
      if (results.length === 0) {
        setSearchResults([]);
        setSearchError('No encontramos ninguna solicitud con el código o teléfono ingresado.');
      } else if (results.length === 1) {
        // Direct match found
        const single = results[0];
        setActiveRequest(single);
        try {
          if (single.orderCode) {
            localStorage.setItem(LOCAL_STORAGE_LAST_CODE, single.orderCode);
            setLastSavedCode(single.orderCode);
          }
          if (single.id) {
            localStorage.setItem(LOCAL_STORAGE_LAST_ID, single.id);
          }
        } catch {}
        setViewMode('status');
        romanticAudio.playBloomSuccess();
      } else {
        // Multiple matches found (e.g. multiple requests with same phone)
        setSearchResults(results);
      }
    } catch (err: any) {
      console.error('Error searching requests in Firestore:', err);
      setSearchError('Ocurrió un error al buscar en Firestore. Por favor intenta de nuevo.');
    } finally {
      setIsSearching(false);
    }
  };

  // Submit corrections / changes requested by admin - AUTOMATICALLY LOCKS UPON SUBMISSION
  const handleSubmitChanges = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeRequest) return;

    setIsSubmittingChanges(true);
    setFormError(null);

    try {
      const now = new Date().toISOString();
      const cleanChangesEmail = changesEmail.trim().toLowerCase();
      const updateData: Partial<MyWebRequest> = {
        applicantName: changesApplicantName.trim() || activeRequest.applicantName,
        partnerName: changesPartnerName.trim() || activeRequest.partnerName,
        email: cleanChangesEmail || activeRequest.email || undefined,
        partner1Email: cleanChangesEmail || activeRequest.partner1Email || undefined,
        anniversaryDate: changesAnniversaryDate || undefined,
        dedication: changesDedication.trim() || undefined,
        userNotes: changesUserNotes.trim() || undefined,
        status: 'en_revision',
        allowUserEditing: false, // Bloqueo automático estricto
        changesLocked: true, // Bloqueo visual del formulario
        changesSubmittedAt: now,
      };

      await submitUserChangesForWebRequest(activeRequest.id, updateData);

      if (onUpdateRequest) {
        await onUpdateRequest(activeRequest.id, updateData);
      }

      // Actualizar estado local inmediatamente para bloquear el formulario y pasar a en_revision
      setActiveRequest((prev) =>
        prev
          ? {
              ...prev,
              ...updateData,
              status: 'en_revision',
              allowUserEditing: false,
              changesLocked: true,
              changesSubmittedAt: now,
            }
          : null
      );

      setIsEditingChanges(false);
      romanticAudio.playBloomSuccess();
      setChangesSuccessMessage('¡Cambios enviados al administrador! La edición ha sido bloqueada automáticamente mientras se validan tus datos.');
      setTimeout(() => {
        setChangesSuccessMessage(null);
      }, 3500);
    } catch (err: any) {
      console.error('Error submitting changes to Firestore:', err);
      setFormError(err?.message || 'No se pudieron enviar los cambios. Intenta nuevamente.');
    } finally {
      setIsSubmittingChanges(false);
    }
  };

  // 5-Stage progress definitions
  const workflowSteps = [
    { num: 1, title: 'Recibido', desc: 'Ingreso al sistema' },
    { num: 2, title: 'En revisión', desc: 'Validación de datos' },
    { num: 3, title: 'Preparación', desc: 'Configuración del sitio' },
    { num: 4, title: 'Aprobado', desc: 'Verificado y listo' },
    { num: 5, title: 'Publicado', desc: 'En línea' },
  ];

  return (
    <div
      className="max-w-2xl mx-auto px-4 pt-3 pb-28 text-[#302B30] font-['Plus_Jakarta_Sans',sans-serif] select-none"
      id="my-web-module-container"
    >
      {/* ======================================================== */}
      {/* CABECERA PRINCIPAL: "Mi Web"                            */}
      {/* ======================================================== */}
      <div className="mb-7 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFF4F7] border border-[#F0DDE4] text-[#E83E78] text-xs font-semibold uppercase tracking-wider mb-2.5">
          <Globe className="w-3.5 h-3.5" />
          <span>Plataforma Romántica</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#302B30] font-['Playfair_Display',serif]">
          Mi Web
        </h1>
        <p className="text-sm sm:text-base text-[#77717A] font-medium mt-1.5 max-w-md mx-auto">
          Crea y consulta tu sitio web personalizado.
        </p>
      </div>

      <AnimatePresence mode="wait">
        {/* ======================================================== */}
        {/* 1. VISTA INICIAL (HOME): ACCIONES PRINCIPALES Y RESUMEN  */}
        {/* ======================================================== */}
        {viewMode === 'home' && (
          <motion.div
            key="view-home"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            {/* DOS ACCIONES PRINCIPALES */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Acción 1: Crear Solicitud */}
              <button
                type="button"
                onClick={() => {
                  romanticAudio.playNote(520, 0.08, 'sine', 0.03);
                  setViewMode('create');
                }}
                className="group relative text-left p-6 rounded-3xl bg-white border border-[#F0DDE4] hover:border-[#E85D8E] shadow-[0_4px_20px_rgba(232,93,142,0.06)] hover:shadow-[0_10px_30px_rgba(232,93,142,0.12)] transition-all duration-300 flex flex-col justify-between cursor-pointer overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFF4F7] rounded-full blur-2xl group-hover:bg-[#FFE8F0] transition-colors pointer-events-none" />
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-[#FFF4F7] border border-[#F0DDE4] text-[#E83E78] flex items-center justify-center group-hover:scale-105 transition-transform">
                      <PlusCircle className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-[#FFF4F7] text-[#E83E78] border border-[#F0DDE4]">
                      Crear nueva
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#302B30] group-hover:text-[#E83E78] transition-colors">
                    Crear solicitud
                  </h3>
                  <p className="text-xs text-[#77717A] mt-2 leading-relaxed">
                    Solicita tu propio sitio con jardín interactivo, fotos, cartas con cera, música romántica y contador en vivo.
                  </p>
                </div>
                <div className="mt-5 flex items-center gap-1.5 text-xs font-bold text-[#E83E78] group-hover:translate-x-1 transition-transform">
                  <span>Iniciar solicitud</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </button>

              {/* Acción 2: Consultar Solicitud */}
              <button
                type="button"
                onClick={() => {
                  romanticAudio.playNote(480, 0.08, 'sine', 0.03);
                  setViewMode('search');
                }}
                className="group relative text-left p-6 rounded-3xl bg-white border border-[#F0DDE4] hover:border-[#E85D8E] shadow-[0_4px_20px_rgba(232,93,142,0.06)] hover:shadow-[0_10px_30px_rgba(232,93,142,0.12)] transition-all duration-300 flex flex-col justify-between cursor-pointer overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#EDE7FF] rounded-full blur-2xl group-hover:bg-[#E2D8FF] transition-colors pointer-events-none" />
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-[#EDE7FF] border border-[#F0DDE4] text-[#E83E78] flex items-center justify-center group-hover:scale-105 transition-transform">
                      <Search className="w-6 h-6" />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-[#EDE7FF] text-[#77717A] border border-[#F0DDE4]">
                      En vivo
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#302B30] group-hover:text-[#E83E78] transition-colors">
                    Consultar solicitud
                  </h3>
                  <p className="text-xs text-[#77717A] mt-2 leading-relaxed">
                    Busca tu solicitud mediante tu número de orden o número de teléfono para verificar su estado en tiempo real.
                  </p>
                </div>
                <div className="mt-5 flex items-center gap-1.5 text-xs font-bold text-[#E83E78] group-hover:translate-x-1 transition-transform">
                  <span>Consultar estado</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </button>
            </div>

            {/* ACCESO RÁPIDO A SOLICITUD ACTIVA O ÚLTIMA CONSULTADA */}
            {activeRequest && (
              <div className="p-5 rounded-3xl bg-white border border-[#F0DDE4] shadow-[0_4px_20px_rgba(232,93,142,0.06)]">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#E83E78] animate-pulse" />
                    <span className="text-xs font-bold text-[#77717A] uppercase tracking-wider">
                      Tu solicitud activa
                    </span>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${statusBadgeStyle.bg} ${statusBadgeStyle.text} ${statusBadgeStyle.border}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${statusBadgeStyle.dot}`} />
                    {statusBadgeStyle.label}
                  </span>
                </div>

                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-sm font-bold text-[#302B30]">
                      {activeRequest.applicantName || 'Novio'} &amp; {activeRequest.partnerName || 'Novia'}
                    </div>
                    <div className="text-xs text-[#77717A] flex items-center gap-2 mt-0.5">
                      <span className="font-mono text-[#E83E78] font-semibold">{activeRequest.orderCode}</span>
                      <span>•</span>
                      <span>Registrada: {formatTimestamp(activeRequest.createdAt)}</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      romanticAudio.playBloomSuccess();
                      setViewMode('status');
                    }}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 text-white font-bold text-xs flex items-center gap-2 shadow-md shadow-pink-500/20 cursor-pointer"
                  >
                    <span>Ver estado en tiempo real</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* CARACTERÍSTICAS INCLUIDAS EN TU WEB */}
            <div className="p-6 rounded-3xl bg-[#FFF4F7] border border-[#F0DDE4]">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-[#E83E78]" />
                <h4 className="text-xs font-extrabold uppercase tracking-wider text-[#302B30]">
                  Todo lo que incluye tu web romántica
                </h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {REAL_AVAILABLE_FEATURES.map((feat) => {
                  const Icon = feat.icon;
                  return (
                    <div
                      key={feat.id}
                      className="p-3.5 rounded-2xl bg-white border border-[#F0DDE4] flex items-start gap-3 shadow-2xs"
                    >
                      <div className={`p-2 rounded-xl border shrink-0 ${feat.accentColor}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-[#302B30]">{feat.label}</div>
                        <div className="text-[11px] text-[#77717A] mt-0.5 leading-snug">
                          {feat.description}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* GARANTÍA DE SINCRONIZACIÓN FIRESTORE */}
            <div className="flex items-center justify-center gap-2 text-xs text-[#77717A] pt-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span>Sincronización en vivo con base de datos Firestore</span>
            </div>
          </motion.div>
        )}

        {/* ======================================================== */}
        {/* 2. VISTA: CREAR SOLICITUD                                */}
        {/* ======================================================== */}
        {viewMode === 'create' && (
          <motion.div
            key="view-create"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            {/* Barra de volver */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  romanticAudio.playNote(400, 0.08, 'sine', 0.03);
                  setViewMode('home');
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-white hover:bg-[#FFF4F7] border border-[#F0DDE4] text-[#302B30] text-xs font-bold transition-colors cursor-pointer shadow-2xs"
              >
                <ArrowLeft className="w-3.5 h-3.5 text-[#E83E78]" />
                <span>Volver a Mi Web</span>
              </button>

              <span className="text-xs text-[#77717A] font-medium">Nueva solicitud</span>
            </div>

            {/* Formulario */}
            <form
              onSubmit={handleCreateRequest}
              className="p-6 sm:p-8 rounded-3xl bg-white border border-[#F0DDE4] shadow-[0_8px_30px_rgba(232,93,142,0.06)] space-y-6"
            >
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-[#302B30] font-['Playfair_Display',serif]">
                  Solicita tu sitio web romántico
                </h2>
                <p className="text-xs sm:text-sm text-[#77717A] mt-1">
                  Completa los datos de ustedes dos. Nuestro equipo configurará cada detalle con amor.
                </p>
              </div>

              {formError && (
                <div className="p-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2.5">
                  <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                  <span>{formError}</span>
                </div>
              )}

              {/* 1. Protagonistas */}
              <div className="space-y-3.5 pt-2">
                <div className="flex items-center gap-2 text-xs font-bold text-[#E83E78] uppercase tracking-wider">
                  <User className="w-3.5 h-3.5" />
                  <span>1. Protagonistas</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div>
                    <label className="block text-xs font-bold text-[#302B30] mb-1.5">
                      Tu nombre (o novio) <span className="text-[#E83E78]">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Ej. Jackson"
                      value={applicantName}
                      onChange={(e) => setApplicantName(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#F0DDE4] text-[#302B30] placeholder-[#77717A]/50 text-xs focus:outline-none focus:border-[#E85D8E] focus:ring-2 focus:ring-[#E85D8E]/15 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#302B30] mb-1.5">
                      Nombre de tu pareja (o novia) <span className="text-[#E83E78]">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Ej. Laura"
                      value={partnerName}
                      onChange={(e) => setPartnerName(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#F0DDE4] text-[#302B30] placeholder-[#77717A]/50 text-xs focus:outline-none focus:border-[#E85D8E] focus:ring-2 focus:ring-[#E85D8E]/15 transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* 2. Contacto y Acceso Administrativo */}
              <div className="space-y-3.5 pt-2 border-t border-[#F0DDE4]">
                <div className="flex items-center gap-2 text-xs font-bold text-[#E83E78] uppercase tracking-wider">
                  <Phone className="w-3.5 h-3.5" />
                  <span>2. Contacto y Acceso al Panel de tu Web</span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#302B30] mb-1.5">
                    WhatsApp o teléfono celular <span className="text-[#E83E78]">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="px-2.5 py-2.5 rounded-xl bg-white border border-[#F0DDE4] text-[#302B30] text-xs focus:outline-none focus:border-[#E85D8E] cursor-pointer"
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.flag} {c.code} ({c.name})
                        </option>
                      ))}
                    </select>

                    <input
                      type="tel"
                      required
                      placeholder="Ej. 300 123 4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="flex-1 px-3.5 py-2.5 rounded-xl bg-white border border-[#F0DDE4] text-[#302B30] placeholder-[#77717A]/50 text-xs focus:outline-none focus:border-[#E85D8E] focus:ring-2 focus:ring-[#E85D8E]/15 transition-all"
                    />
                  </div>
                  <p className="text-[11px] text-[#77717A] mt-1">
                    Te enviaremos el enlace web y avisos de aprobación por este medio.
                  </p>
                </div>

                <div className="pt-1">
                  <label className="block text-xs font-bold text-[#302B30] mb-1.5 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-[#E85D8E]" />
                    <span>Correo electrónico para tu cuenta de acceso <span className="text-[#E83E78]">*</span></span>
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="Ej. tucorreo@ejemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#F0DDE4] text-[#302B30] placeholder-[#77717A]/50 text-xs focus:outline-none focus:border-[#E85D8E] focus:ring-2 focus:ring-[#E85D8E]/15 transition-all"
                  />
                  <p className="text-[11px] text-[#77717A] mt-1 leading-relaxed">
                    Con este correo accederás al panel de administración de tu página web. Al ingresar por primera vez, solo tendrás que asignarle tu contraseña para acceder.
                  </p>
                </div>
              </div>

              {/* 3. Detalles Románticos (Opcionales) */}
              <div className="space-y-3.5 pt-2 border-t border-[#F0DDE4]">
                <div className="flex items-center gap-2 text-xs font-bold text-[#E85D8E] uppercase tracking-wider">
                  <Heart className="w-3.5 h-3.5" />
                  <span>3. Detalles Románticos (Opcionales)</span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#302B30] mb-1.5">
                    Fecha de inicio o aniversario
                  </label>
                  <input
                    type="date"
                    value={anniversaryDate}
                    onChange={(e) => setAnniversaryDate(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#F0DDE4] text-[#302B30] text-xs focus:outline-none focus:border-[#E85D8E] focus:ring-2 focus:ring-[#E85D8E]/15 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#302B30] mb-1.5">
                    Frase o dedicatoria para la portada
                  </label>
                  <input
                    type="text"
                    placeholder="Ej. Desde el primer día supe que serías para siempre..."
                    value={dedication}
                    onChange={(e) => setDedication(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#F0DDE4] text-[#302B30] placeholder-[#77717A]/50 text-xs focus:outline-none focus:border-[#E85D8E] focus:ring-2 focus:ring-[#E85D8E]/15 transition-all"
                  />
                </div>
              </div>

              {/* 4. Módulos Deseados */}
              <div className="space-y-3 pt-2 border-t border-[#F0DDE4]">
                <div className="flex items-center gap-2 text-xs font-bold text-[#E83E78] uppercase tracking-wider">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>4. Módulos que deseas incluir</span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {REAL_AVAILABLE_FEATURES.map((feat) => {
                    const isSelected = selectedFeatures.includes(feat.id);
                    const Icon = feat.icon;
                    return (
                      <button
                        key={feat.id}
                        type="button"
                        onClick={() => handleToggleFeature(feat.id)}
                        className={`p-3 rounded-2xl border text-left flex flex-col justify-between transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-[#FFF4F7] border-2 border-[#E85D8E] text-[#302B30] shadow-sm'
                            : 'bg-white border-[#F0DDE4] text-[#77717A] hover:border-[#E85D8E]/40 hover:bg-[#FFFDFB]'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Icon className={`w-4 h-4 ${isSelected ? 'text-[#E83E78]' : 'text-[#77717A]'}`} />
                          <div
                            className={`w-4 h-4 rounded-md flex items-center justify-center text-[10px] ${
                              isSelected ? 'bg-[#E83E78] text-white font-bold' : 'border border-[#F0DDE4]'
                            }`}
                          >
                            {isSelected && <Check className="w-3 h-3" />}
                          </div>
                        </div>
                        <span className={`text-xs font-bold truncate ${isSelected ? 'text-[#302B30]' : 'text-[#77717A]'}`}>
                          {feat.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 5. Notas Adicionales */}
              <div className="space-y-2 pt-2 border-t border-[#F0DDE4]">
                <label className="block text-xs font-bold text-[#302B30]">
                  Notas u observaciones adicionales (Opcional)
                </label>
                <textarea
                  rows={2}
                  placeholder="Detalles sobre colores favoritos, temática o ideas especiales..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#F0DDE4] text-[#302B30] placeholder-[#77717A]/50 text-xs focus:outline-none focus:border-[#E85D8E] focus:ring-2 focus:ring-[#E85D8E]/15 transition-all resize-none"
                />
              </div>

              {/* Botón de Envío */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-pink-500/25 transition-all disabled:opacity-50 cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Registrando solicitud en Firestore...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Enviar solicitud ahora</span>
                  </>
                )}
              </button>
            </form>
          </motion.div>
        )}

        {/* ======================================================== */}
        {/* 3. VISTA: SOLICITUD CREADA EXITOSAMENTE (SUCCESS)        */}
        {/* ======================================================== */}
        {viewMode === 'created_success' && createdRequestInfo && (
          <motion.div
            key="view-created-success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="p-6 sm:p-8 rounded-3xl bg-white border border-[#F0DDE4] text-center space-y-6 shadow-xl"
          >
            <div className="w-16 h-16 rounded-3xl bg-emerald-50 border border-emerald-200 text-emerald-600 flex items-center justify-center mx-auto shadow-sm">
              <CheckCircle className="w-8 h-8" />
            </div>

            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200">
                ¡Registrada en Firestore!
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#302B30] font-['Playfair_Display',serif] mt-3">
                ¡Solicitud registrada con éxito!
              </h2>
              <p className="text-xs sm:text-sm text-[#77717A] mt-2 max-w-md mx-auto">
                Hemos recibido tu solicitud para{' '}
                <strong className="text-[#302B30]">
                  {createdRequestInfo.applicantName} &amp; {createdRequestInfo.partnerName}
                </strong>
                . Guarda tu código para consultar el estado en cualquier momento.
              </p>
            </div>

            {/* Tarjeta del código de orden */}
            <div className="p-5 rounded-2xl bg-[#FFF4F7] border border-[#F0DDE4] max-w-sm mx-auto shadow-2xs">
              <div className="text-[11px] font-semibold text-[#77717A] uppercase tracking-wider mb-1">
                Tu código de solicitud
              </div>
              <div className="text-2xl sm:text-3xl font-black font-mono tracking-widest text-[#E83E78] select-all my-1">
                {createdRequestInfo.orderCode}
              </div>
              <button
                type="button"
                onClick={() => handleCopyCode(createdRequestInfo.orderCode)}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-[#FFF4F7] border border-[#F0DDE4] text-[#E83E78] text-xs font-bold transition-colors cursor-pointer shadow-2xs"
              >
                {copiedCode ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                    <span className="text-emerald-700">¡Código copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    <span>Copiar código de orden</span>
                  </>
                )}
              </button>
            </div>

            {/* Próximos pasos */}
            <div className="p-4 rounded-2xl bg-[#F8F5F7] border border-[#F0DDE4] text-left text-xs space-y-2 text-[#302B30] max-w-md mx-auto">
              <div className="font-bold text-[#302B30] flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#E83E78]" />
                <span>¿Qué sigue ahora?</span>
              </div>
              <p className="text-[11px] text-[#77717A]">
                1. Nuestro equipo revisará los datos registrados.<br />
                2. Podrás consultar el avance con este código o tu teléfono.<br />
                3. Si se necesitan ajustes, podrás responder directamente en pantalla.
              </p>
            </div>

            {/* Botones de acción */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  romanticAudio.playBloomSuccess();
                  setViewMode('status');
                }}
                className="w-full sm:w-auto px-6 py-3 rounded-2xl bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-md shadow-pink-500/20 cursor-pointer"
              >
                <span>Ver estado de mi solicitud en tiempo real</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => {
                  romanticAudio.playNote(400, 0.08, 'sine', 0.03);
                  setViewMode('home');
                }}
                className="w-full sm:w-auto px-5 py-3 rounded-2xl bg-white hover:bg-[#FFF4F7] border border-[#F0DDE4] text-[#302B30] font-bold text-xs cursor-pointer shadow-2xs"
              >
                Volver al inicio
              </button>
            </div>
          </motion.div>
        )}

        {/* ======================================================== */}
        {/* 4. VISTA: CONSULTAR SOLICITUD (BÚSQUEDA)                  */}
        {/* ======================================================== */}
        {viewMode === 'search' && (
          <motion.div
            key="view-search"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            {/* Barra de volver */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  romanticAudio.playNote(400, 0.08, 'sine', 0.03);
                  setViewMode('home');
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-[#FFF4F7] border border-[#F0DDE4] text-[#302B30] text-xs font-bold transition-colors cursor-pointer shadow-2xs"
              >
                <ArrowLeft className="w-3.5 h-3.5 text-[#E83E78]" />
                <span>Volver a Mi Web</span>
              </button>

              <span className="text-xs text-[#77717A] font-medium">Búsqueda en Firestore</span>
            </div>

            {/* Tarjeta de búsqueda */}
            <div className="p-6 sm:p-8 rounded-3xl bg-white border border-[#F0DDE4] shadow-xl space-y-5">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-[#302B30] font-['Playfair_Display',serif]">
                  Consultar estado de solicitud
                </h2>
                <p className="text-xs sm:text-sm text-[#77717A] mt-1">
                  Ingresa tu número de orden (ej. <span className="text-[#E83E78] font-mono font-bold">WEB-8241</span> o <span className="text-[#E83E78] font-mono font-bold">8241</span>) o tu número de WhatsApp para ver el estado en tiempo real.
                </p>
              </div>

              {/* Formulario de búsqueda */}
              <form onSubmit={(e) => handlePerformSearch(e)} className="space-y-3">
                <div className="relative">
                  <Search className="w-4 h-4 text-[#77717A] absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    required
                    placeholder="Número de orden (WEB-...) o WhatsApp (300...)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-24 py-3 rounded-2xl bg-white border border-[#F0DDE4] text-[#302B30] placeholder-[#77717A]/50 text-xs focus:outline-none focus:border-[#E85D8E] focus:ring-2 focus:ring-[#E85D8E]/15 transition-all font-mono"
                  />
                  <button
                    type="submit"
                    disabled={isSearching}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 text-white font-bold text-xs flex items-center gap-1.5 transition-colors disabled:opacity-50 cursor-pointer shadow-md shadow-pink-500/20"
                  >
                    {isSearching ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <span>Buscar</span>
                    )}
                  </button>
                </div>

                {/* Acceso rápido a última solicitud guardada si existe */}
                {lastSavedCode && (
                  <div className="flex items-center gap-2 pt-1 text-xs">
                    <span className="text-[#77717A]">Última guardada:</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery(lastSavedCode);
                        handlePerformSearch(undefined, lastSavedCode);
                      }}
                      className="px-2.5 py-0.5 rounded-lg bg-[#FFF4F7] border border-[#F0DDE4] text-[#E83E78] font-mono font-bold text-[11px] hover:bg-[#FFE8F0] transition-colors cursor-pointer"
                    >
                      {lastSavedCode}
                    </button>
                  </div>
                )}
              </form>

              {/* Error si no se encuentra */}
              {searchError && (
                <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-xs space-y-2">
                  <div className="flex items-center gap-2 text-amber-800 font-bold">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>{searchError}</span>
                  </div>
                  <p className="text-[11px] text-amber-900 leading-relaxed">
                    Verifica que hayas escrito correctamente el código o número de teléfono registrado. Si aún no has solicitado tu web, puedes crear una ahora mismo.
                  </p>
                  <div className="pt-2 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setViewMode('create')}
                      className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#E85D8E] to-[#E83E78] text-white font-bold text-[11px] cursor-pointer shadow-2xs"
                    >
                      Crear nueva solicitud
                    </button>
                  </div>
                </div>
              )}

              {/* Lista si hay múltiples resultados (ej. mismo teléfono para varias solicitudes) */}
              {searchResults && searchResults.length > 1 && (
                <div className="space-y-3 pt-3 border-t border-[#F0DDE4]">
                  <div className="text-xs font-bold text-[#302B30]">
                    Se encontraron {searchResults.length} solicitudes asociadas:
                  </div>

                  <div className="space-y-2">
                    {searchResults.map((req) => (
                      <div
                        key={req.id}
                        className="p-3.5 rounded-2xl bg-white border border-[#F0DDE4] hover:border-[#E85D8E] flex items-center justify-between gap-3 transition-colors shadow-2xs"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-[#E83E78] text-xs">
                              {req.orderCode}
                            </span>
                            <span className="text-[11px] text-[#302B30] font-medium">
                              {req.applicantName} &amp; {req.partnerName}
                            </span>
                          </div>
                          <div className="text-[10px] text-[#77717A] mt-0.5">
                            {formatTimestamp(req.createdAt)}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            setActiveRequest(req);
                            setViewMode('status');
                            romanticAudio.playBloomSuccess();
                          }}
                          className="px-3 py-1.5 rounded-xl bg-[#FFF4F7] hover:bg-[#FFE8F0] border border-[#F0DDE4] text-[#E83E78] text-xs font-bold transition-colors cursor-pointer"
                        >
                          Ver estado
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ======================================================== */}
        {/* 5. VISTA: ESTADO DE LA SOLICITUD EN TIEMPO REAL          */}
        {/* ======================================================== */}
        {viewMode === 'status' && activeRequest && (
          <motion.div
            key="view-status"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            {/* Barra de volver y estado en vivo */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  romanticAudio.playNote(400, 0.08, 'sine', 0.03);
                  setViewMode('home');
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-white hover:bg-[#FFF4F7] border border-[#F0DDE4] text-[#302B30] text-xs font-bold transition-colors cursor-pointer shadow-2xs"
              >
                <ArrowLeft className="w-3.5 h-3.5 text-[#E83E78]" />
                <span>Volver a Mi Web</span>
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setViewMode('search')}
                  className="px-2.5 py-1 rounded-lg bg-white hover:bg-[#FFF4F7] text-[#77717A] hover:text-[#302B30] text-[11px] font-medium border border-[#F0DDE4] cursor-pointer"
                >
                  Buscar otra
                </button>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span>En vivo</span>
                </div>
              </div>
            </div>

            {/* TARJETA CABECERA DE LA SOLICITUD */}
            <div className="p-6 rounded-3xl bg-white border border-[#F0DDE4] shadow-xl relative overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#77717A] font-medium">Solicitud</span>
                  <span className="px-2.5 py-0.5 rounded-lg bg-[#FFF4F7] border border-[#F0DDE4] font-mono font-bold text-[#E83E78] text-xs select-all">
                    {activeRequest.orderCode}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopyCode(activeRequest.orderCode)}
                    title="Copiar código de solicitud"
                    className="p-1 rounded-md text-[#77717A] hover:text-[#302B30] transition-colors cursor-pointer"
                  >
                    {copiedCode ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>

                <div
                  className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border ${statusBadgeStyle.bg} ${statusBadgeStyle.text} ${statusBadgeStyle.border}`}
                >
                  <span className={`w-2 h-2 rounded-full ${statusBadgeStyle.dot}`} />
                  <span>{statusBadgeStyle.label}</span>
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-[#302B30] font-['Playfair_Display',serif]">
                {activeRequest.applicantName} &amp; {activeRequest.partnerName}
              </h2>

              <p className="text-xs text-[#77717A] mt-1">
                Registrada el {formatTimestamp(activeRequest.createdAt)}
              </p>

              {/* LÍNEA DE PROGRESO DE 5 FASES */}
              <div className="mt-6 pt-5 border-t border-[#F0DDE4]">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#77717A] mb-3 flex items-center justify-between">
                  <span>Flujo del proceso</span>
                  <span className="text-[#E83E78]">
                    Fase {phaseInfo.phase} de 5
                  </span>
                </div>

                <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                  {workflowSteps.map((step) => {
                    const isCompleted = phaseInfo.phase >= step.num;
                    const isCurrent = phaseInfo.phase === step.num;
                    return (
                      <div key={step.num} className="text-center">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            isCompleted
                              ? isCurrent
                                ? 'bg-[#E83E78] shadow-md shadow-pink-500/30'
                                : 'bg-emerald-500'
                              : 'bg-[#F0DDE4]'
                          }`}
                        />
                        <div
                          className={`text-[10px] mt-1.5 truncate font-medium ${
                            isCurrent
                              ? 'text-[#E83E78] font-bold'
                              : isCompleted
                              ? 'text-emerald-700'
                              : 'text-[#77717A]'
                          }`}
                        >
                          {step.title}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ======================================================== */}
            {/* DETALLES DINÁMICOS SEGÚN EL ESTADO ACTUAL               */}
            {/* ======================================================== */}

            {/* 1. PUBLICADO: Sitio web activo */}
            {activeRequest.status === 'publicado' && (
              <div className="p-6 rounded-3xl bg-emerald-50/70 border border-emerald-200 shadow-xl space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-100 border border-emerald-200 text-emerald-700 flex items-center justify-center shrink-0 shadow-2xs">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-emerald-950">¡Tu sitio web está en línea!</h3>
                    <p className="text-xs text-emerald-800 mt-0.5">
                      Tu web interactiva ya fue aprobada y desplegada. Ya puedes compartirla y disfrutarla con tu pareja.
                    </p>
                  </div>
                </div>

                {/* Enlace destacado */}
                <div className="p-4 rounded-2xl bg-white border border-emerald-200 space-y-2 shadow-2xs">
                  <div className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
                    Enlace de tu sitio web
                  </div>
                  <div className="flex items-center justify-between gap-2 bg-emerald-50/60 p-2.5 rounded-xl border border-emerald-200">
                    <span className="text-xs font-mono text-emerald-900 truncate select-all font-semibold">
                      {personalWebUrl}
                    </span>
                    <button
                      type="button"
                      onClick={handleCopyLink}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shrink-0 flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
                    >
                      {copiedLink ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copiedLink ? 'Copiado' : 'Copiar'}</span>
                    </button>
                  </div>
                </div>

                {/* Botones de acción del sitio publicado */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
                  {onOpenPersonalizedWeb && (
                    <button
                      type="button"
                      onClick={() => onOpenPersonalizedWeb(activeRequest)}
                      className="py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:opacity-95 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-md shadow-emerald-700/20 cursor-pointer"
                    >
                      <Eye className="w-4 h-4" />
                      <span>Abrir mi web</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleShareWhatsApp}
                    className="py-3 px-4 rounded-xl bg-[#25D366] hover:bg-[#20ba58] text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-md shadow-emerald-600/15 transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>Compartir WhatsApp</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowQrModal(true)}
                    className="py-3 px-4 rounded-xl bg-white hover:bg-[#FFF4F7] border border-[#F0DDE4] text-[#302B30] font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-2xs transition-colors"
                  >
                    <QrCode className="w-4 h-4 text-[#E83E78]" />
                    <span>Ver Código QR</span>
                  </button>
                </div>
              </div>
            )}

            {/* 2. CAMBIOS SOLICITADOS: Requiere atención */}
            {activeRequest.status === 'cambios_solicitados' && (
              <div className="p-6 rounded-3xl bg-amber-50/80 border border-amber-200 shadow-xl space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-amber-100 border border-amber-200 text-amber-700 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-amber-900">
                      El administrador solicitó algunos ajustes
                    </h3>
                    <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                      Revisa la siguiente observación y actualiza tus datos para que podamos continuar con la aprobación de tu sitio.
                    </p>
                  </div>
                </div>

                {/* Nota del administrador */}
                {activeRequest.adminNotes && (
                  <div className="p-4 rounded-2xl bg-white border border-amber-200 text-xs shadow-2xs">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800 mb-1">
                      Mensaje del administrador:
                    </div>
                    <div className="text-[#302B30] italic">
                      "{activeRequest.adminNotes}"
                    </div>
                  </div>
                )}

                {/* Formulario de correcciones */}
                {!isEditingChanges ? (
                  <button
                    type="button"
                    onClick={() => setIsEditingChanges(true)}
                    className="w-full py-3 rounded-2xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-md shadow-amber-700/20"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Corregir o responder cambios</span>
                  </button>
                ) : (
                  <form onSubmit={handleSubmitChanges} className="space-y-4 pt-2">
                    {changesSuccessMessage && (
                      <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600" />
                        <span>{changesSuccessMessage}</span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold text-[#302B30] mb-1">
                          Tu nombre
                        </label>
                        <input
                          type="text"
                          value={changesApplicantName}
                          onChange={(e) => setChangesApplicantName(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-amber-200 text-[#302B30] text-xs focus:outline-none focus:border-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-[#302B30] mb-1">
                          Nombre de tu pareja
                        </label>
                        <input
                          type="text"
                          value={changesPartnerName}
                          onChange={(e) => setChangesPartnerName(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-amber-200 text-[#302B30] text-xs focus:outline-none focus:border-amber-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold text-[#302B30] mb-1">
                          Fecha de aniversario
                        </label>
                        <input
                          type="date"
                          value={changesAnniversaryDate}
                          onChange={(e) => setChangesAnniversaryDate(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-amber-200 text-[#302B30] text-xs focus:outline-none focus:border-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-[#302B30] mb-1 flex items-center gap-1">
                          <Mail className="w-3 h-3 text-[#E85D8E]" />
                          <span>Correo electrónico de acceso</span>
                        </label>
                        <input
                          type="email"
                          required
                          value={changesEmail}
                          onChange={(e) => setChangesEmail(e.target.value)}
                          placeholder="tu@correo.com"
                          className="w-full px-3 py-2 rounded-xl bg-white border border-amber-200 text-[#302B30] text-xs focus:outline-none focus:border-amber-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-[#302B30] mb-1">
                        Dedicatoria de portada
                      </label>
                      <input
                        type="text"
                        value={changesDedication}
                        onChange={(e) => setChangesDedication(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-white border border-amber-200 text-[#302B30] text-xs focus:outline-none focus:border-amber-500"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-[#302B30] mb-1">
                        Tu respuesta al administrador
                      </label>
                      <textarea
                        rows={2}
                        placeholder="Ej. He actualizado los datos y verificado mi correo de acceso."
                        value={changesUserNotes}
                        onChange={(e) => setChangesUserNotes(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-white border border-amber-200 text-[#302B30] text-xs focus:outline-none focus:border-amber-500 resize-none"
                      />
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="submit"
                        disabled={isSubmittingChanges}
                        className="flex-1 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50 cursor-pointer shadow-2xs"
                      >
                        {isSubmittingChanges ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5" />
                        )}
                        <span>Enviar cambios al administrador</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setIsEditingChanges(false)}
                        className="px-3 py-2.5 rounded-xl bg-white hover:bg-[#FFF4F7] border border-[#F0DDE4] text-[#302B30] text-xs font-bold cursor-pointer shadow-2xs"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {/* 3. EN REVISIÓN */}
            {activeRequest.status === 'en_revision' && (
              <div className="p-6 rounded-3xl bg-[#EDE7FF]/60 border border-[#F0DDE4] shadow-xl space-y-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-[#EDE7FF] border border-[#F0DDE4] text-purple-700 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 animate-pulse" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#302B30]">En proceso de revisión</h3>
                    <p className="text-xs text-[#77717A] mt-0.5">
                      Estamos verificando los datos proporcionados y organizando la estructura de tu web personalizada.
                    </p>
                  </div>
                </div>

                {/* Aviso de bloqueo automático de cambios */}
                {activeRequest.changesSubmittedAt && (
                  <div className="p-4 rounded-2xl bg-white border border-purple-200/80 text-xs shadow-2xs space-y-2 mt-1">
                    <div className="flex items-center gap-2 text-purple-900 font-bold">
                      <Lock className="w-4 h-4 text-purple-600 shrink-0" />
                      <span>Cambios enviados — Edición bloqueada automáticamente</span>
                    </div>
                    <p className="text-[#555] text-[11px] leading-relaxed">
                      Enviaste tus modificaciones el <strong className="text-stone-800">{formatTimestamp(activeRequest.changesSubmittedAt)}</strong>. La edición ha sido bloqueada automáticamente para evitar alteraciones mientras el administrador valida la nueva información. Si se requiere algún ajuste adicional, el administrador volverá a habilitar la edición solicitando cambios.
                    </p>
                    {activeRequest.userNotes && (
                      <div className="p-2.5 rounded-xl bg-purple-50/70 border border-purple-100 text-[11px] text-purple-950">
                        <span className="font-semibold text-purple-900">Tu respuesta enviada:</span> "{activeRequest.userNotes}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 4. RECIBIDO / ENVIADO */}
            {(activeRequest.status === 'recibido' || activeRequest.status === 'enviado' || (activeRequest.status as any) === 'pending') && (
              <div className="p-6 rounded-3xl bg-[#FFF4F7] border border-[#F0DDE4] shadow-xl space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-white border border-[#F0DDE4] text-[#E83E78] flex items-center justify-center shrink-0 shadow-2xs">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-[#302B30]">Solicitud recibida</h3>
                    <p className="text-xs text-[#77717A] mt-0.5">
                      Tu solicitud está en la cola de atención. Muy pronto nuestro equipo iniciará la revisión de tus datos.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 5. APROBADO: En preparación final */}
            {(activeRequest.status === 'aprobado' || activeRequest.status === 'approved') && (
              <div className="p-6 rounded-3xl bg-emerald-50/70 border border-emerald-200 shadow-xl space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-100 border border-emerald-200 text-emerald-700 flex items-center justify-center shrink-0">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-emerald-950">¡Solicitud aprobada!</h3>
                    <p className="text-xs text-emerald-800 mt-0.5">
                      Tu sitio web ha sido verificado y aprobado. Estamos preparando el despliegue final para que esté completamente en línea.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 6. RECHAZADO */}
            {(activeRequest.status === 'rechazado' || activeRequest.status === 'rejected') && (
              <div className="p-6 rounded-3xl bg-rose-50 border border-rose-200 shadow-xl space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-rose-100 border border-rose-200 text-rose-700 flex items-center justify-center shrink-0">
                    <AlertCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-rose-900">Solicitud no procesada</h3>
                    <p className="text-xs text-rose-800 mt-0.5">
                      {activeRequest.adminNotes || 'La solicitud no pudo ser aprobada debido a inconsistencias en la información.'}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setViewMode('create')}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#E85D8E] to-[#E83E78] text-white font-bold text-xs cursor-pointer shadow-2xs"
                >
                  Crear una nueva solicitud
                </button>
              </div>
            )}

            {/* RESUMEN DE DATOS REGISTRADOS */}
            <div className="p-6 rounded-3xl bg-[#FFF4F7] border border-[#F0DDE4] space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#E83E78]" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-[#302B30]">
                  Resumen de la solicitud
                </h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="p-3 rounded-xl bg-white border border-[#F0DDE4] shadow-2xs">
                  <span className="text-[10px] text-[#77717A] uppercase tracking-wider block">
                    Pareja
                  </span>
                  <span className="font-bold text-[#302B30] mt-0.5 block">
                    {activeRequest.applicantName} &amp; {activeRequest.partnerName}
                  </span>
                </div>

                <div className="p-3 rounded-xl bg-white border border-[#F0DDE4] shadow-2xs">
                  <span className="text-[10px] text-[#77717A] uppercase tracking-wider block">
                    WhatsApp de contacto
                  </span>
                  <span className="font-mono text-[#302B30] font-semibold mt-0.5 block">
                    {activeRequest.whatsappPhone}
                  </span>
                </div>

                {activeRequest.anniversaryDate && (
                  <div className="p-3 rounded-xl bg-white border border-[#F0DDE4] shadow-2xs">
                    <span className="text-[10px] text-[#77717A] uppercase tracking-wider block">
                      Fecha especial
                    </span>
                    <span className="text-[#302B30] font-medium mt-0.5 block">
                      {activeRequest.anniversaryDate}
                    </span>
                  </div>
                )}

                {(activeRequest.email || activeRequest.partner1Email) && (
                  <div className="p-3 rounded-xl bg-white border border-[#F0DDE4] shadow-2xs">
                    <span className="text-[10px] text-[#77717A] uppercase tracking-wider block flex items-center gap-1">
                      <Mail className="w-3 h-3 text-[#E85D8E]" />
                      <span>Correo de acceso</span>
                    </span>
                    <span className="text-[#302B30] font-medium mt-0.5 block truncate">
                      {activeRequest.email || activeRequest.partner1Email}
                    </span>
                  </div>
                )}
              </div>

              {/* Módulos solicitados */}
              {activeRequest.desiredFeatures && activeRequest.desiredFeatures.length > 0 && (
                <div className="pt-2">
                  <span className="text-[10px] text-[#77717A] uppercase tracking-wider block mb-2 font-semibold">
                    Módulos solicitados
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {activeRequest.desiredFeatures.map((featId) => {
                      const def = REAL_AVAILABLE_FEATURES.find((f) => f.id === featId);
                      return (
                        <span
                          key={featId}
                          className="px-2.5 py-1 rounded-lg bg-white border border-[#F0DDE4] text-[11px] text-[#302B30] font-medium shadow-2xs"
                        >
                          {def ? def.label : featId}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ======================================================== */}
      {/* MODAL: CÓDIGO QR INTERACTIVO                             */}
      {/* ======================================================== */}
      <AnimatePresence>
        {showQrModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/30 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="p-6 rounded-3xl bg-white border border-[#F0DDE4] max-w-sm w-full text-center space-y-4 shadow-2xl relative text-[#302B30]"
            >
              <button
                type="button"
                onClick={() => setShowQrModal(false)}
                className="absolute top-4 right-4 p-1.5 rounded-full bg-[#FFF4F7] hover:bg-[#FFE8F0] text-[#77717A] hover:text-[#302B30] transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="w-10 h-10 rounded-2xl bg-[#FFF4F7] border border-[#F0DDE4] text-[#E83E78] flex items-center justify-center mx-auto shadow-2xs">
                <QrCode className="w-5 h-5" />
              </div>

              <div>
                <h3 className="text-lg font-bold text-[#302B30] font-['Playfair_Display',serif]">
                  Código QR de tu web
                </h3>
                <p className="text-xs text-[#77717A] mt-0.5">
                  Escanéalo con la cámara de cualquier teléfono para abrir tu sitio directamente.
                </p>
              </div>

              {qrCodeImageUrl && (
                <div className="p-3 bg-[#FFF4F7] rounded-2xl inline-block border border-[#F0DDE4] shadow-xs my-2">
                  <img
                    src={qrCodeImageUrl}
                    alt="Código QR del sitio web"
                    className="w-48 h-48 rounded-lg"
                  />
                </div>
              )}

              <div className="text-[11px] font-mono text-[#77717A] break-all px-2">
                {personalWebUrl}
              </div>

              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#E85D8E] to-[#E83E78] hover:opacity-95 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-md shadow-pink-500/20 transition-all"
                >
                  {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span>{copiedLink ? '¡Enlace copiado!' : 'Copiar enlace directo'}</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
